import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SellerNavbar from "../components/SellerNavbar";
import { api } from "../services/api";
import "./SellerProfile.css";

const PHONE_REGEX = /^\+?\d{7,15}$/;

const normalizePhone = (value) => value.replace(/[^\d+]/g, "").trim();

const validateProfileForm = (form) => {
  const errors = {};

  if (!form.name || !form.name.trim()) {
    errors.name = "Full name is required";
  } else if (form.name.length < 3) {
    errors.name = "Full name must be at least 3 characters";
  }

  const phone = normalizePhone(form.phone);
  if (!phone) {
    errors.phone = "Phone number is required";
  } else if (!PHONE_REGEX.test(phone)) {
    errors.phone = "Phone must be 7-15 digits (optional leading +)";
  }

  return errors;
};

export default function SellerProfile() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Seller Profile | Balitah";

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    descriptionTag.setAttribute(
      "content",
      "Manage your seller profile on Balitah, a local Bruneian e-commerce platform.",
    );
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      const res = await api.getMe();

      if (res.success) {
        setForm({
          name: res.data.name || "",
          email: res.data.email || "",
          phone: res.data.phone || "",
          role: res.data.role || "",
        });
      } else {
        alert(res.message || "Failed to load profile");
      }

      setLoading(false);
    };

    loadProfile();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = validateProfileForm(form);

    if (Object.keys(errors).length > 0) {
      setProfileErrors(errors);
      return;
    }

    setSaving(true);

    const res = await api.updateMe({
      name: form.name.trim(),
      phone: normalizePhone(form.phone),
    });

    setSaving(false);

    if (res.success) {
      alert("Profile updated");
    } else {
      alert(res.message || "Failed to update profile");
    }
  };

  const handleLogout = async () => {
    const res = await api.logout();

    if (res.success) {
      navigate("/login");
    } else {
      alert(res.message || "Failed to logout");
    }
  };

  return (
    <div className="sp-page">
      <SellerNavbar />

      <div className="sp-container">
        {/* Page Header */}
        <div className="sp-header">
          <h1>Profile</h1>
          <p>Manage your seller account information</p>
        </div>

        {loading ? (
          <div className="sp-loading">
            <div className="sp-spinner"></div>
            <p>Loading profile...</p>
          </div>
        ) : (
          <div className="sp-content">
            <form className="sp-form sp-section" onSubmit={handleSave}>
              <div className="sp-section-header">
                <div className="sp-section-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <div>
                  <h2>Profile Information</h2>
                  <p>Update your personal details</p>
                </div>
              </div>

              <div className="sp-input-group">
                <label htmlFor="sp-name">Full Name</label>
                <input
                  id="sp-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm((prev) => ({ ...prev, name: value }));
                    setProfileErrors((current) => ({
                      ...current,
                      name: value.trim() ? "" : "Full name is required",
                    }));
                  }}
                  placeholder="Enter your full name"
                />
                {profileErrors.name ? (
                  <div className="sp-error">{profileErrors.name}</div>
                ) : null}
              </div>

              <div className="sp-input-group">
                <label htmlFor="sp-email">Email</label>
                <input
                  id="sp-email"
                  type="email"
                  value={form.email}
                  readOnly
                  className="sp-readonly"
                />
                <span className="sp-hint">Email cannot be changed</span>
              </div>

              <div className="sp-input-group">
                <label htmlFor="sp-phone">Phone</label>
                <input
                  id="sp-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => {
                    const value = e.target.value;
                    const normalized = normalizePhone(value);
                    setForm((prev) => ({ ...prev, phone: value }));
                    setProfileErrors((current) => {
                      if (!normalized) {
                        return {
                          ...current,
                          phone: "Phone number is required",
                        };
                      }
                      if (!PHONE_REGEX.test(normalized)) {
                        return {
                          ...current,
                          phone: "Phone must be 7-15 digits",
                        };
                      }
                      return { ...current, phone: "" };
                    });
                  }}
                  placeholder="+1234567890"
                />
                {profileErrors.phone ? (
                  <div className="sp-error">{profileErrors.phone}</div>
                ) : null}
              </div>

              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  type="submit"
                  className="sp-btn sp-btn-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="sp-btn-spinner"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                        <line x1="17" y1="6" x2="17" y2="14" />
                        <line x1="14" y1="9" x2="20" y2="9" />
                      </svg>
                      Save Changes
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="sp-btn sp-btn-secondary"
                  onClick={handleLogout}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Logout
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
